/**
 * What the next publish changes (GET /{id}/changes): new / edited / removed pages and site-wide settings. For publish dialogs:
 * <PublishChanges open={open} />. Loads each time the dialog opens; renders nothing until loaded.
 */
import React, { useEffect, useState } from 'react';
import { List, Tag, Typography } from 'antd';
import { useTranslation } from '../../../shared/i18n';
import { QUIET } from './api';
import { useSite } from './common';

const { Text } = Typography;
const COLOR = { new: 'green', edited: 'gold', removed: 'red' };

const PublishChanges = ({ open, style, api: apiProp }) => {
    const { t } = useTranslation();
    const ctx = useSite();
    const api = apiProp || ctx.api; // the editor is outside SiteContext and passes its own
    const [data, setData] = useState(null);
    useEffect(() => {
        if (!open) return undefined;
        let live = true;
        setData(null);
        api.get('/changes', null, QUIET).then((d) => live && setData(d)).catch(() => live && setData(null));
        return () => { live = false; };
    }, [open, api]);
    if (!data) return null;
    const label = { new: t('New'), edited: t('Edited'), removed: t('Removed') };
    const rows = [
        ...(data.items || []).map((p) => ({ key: `p${p.id}`, title: p.title || p.path, path: p.path, change: p.change })),
        ...(data.removed || []).map((p) => ({ key: `r${p.path}`, title: p.title || p.path, path: p.path, change: 'removed' })),
    ];
    if (!rows.length && !data.site) {
        return <Text type="secondary" style={{ display: 'block', marginBottom: 12, ...style }}>{t('No changes since the last publish')}</Text>;
    }
    return (
        <div style={{ marginBottom: 12, ...style }} data-testid="publish-changes">
            <Text strong>{t('Changes to publish')}</Text>
            <List size="small" style={{ maxHeight: 220, overflow: 'auto' }} dataSource={rows} renderItem={(r) => (
                <List.Item style={{ padding: '4px 0' }}>
                    <span><Tag color={COLOR[r.change]}>{label[r.change]}</Tag>{r.title} <Text type="secondary" code>{r.path}</Text></span>
                </List.Item>
            )} />
            {data.site && <Text type="secondary" style={{ fontSize: 12 }}>{t('Site settings, theme or global blocks changed')}</Text>}
        </div>
    );
};

export default PublishChanges;
